import React, {Component, useState} from 'react';
import PropTypes from 'prop-types';
import sortBy from 'lodash/sortBy';

import Loader from '../Loader';
import Checkbox from '../Checkbox';
import Button from '../Button';

import './style.scss';

function RenderRow({row, fields, selectable, selectedKeys, selection, onRowSelect, setSelectedKeys, setSelection}) {
	const [expand, setExpand] = useState(false);
	const [message, setMessage] = useState("Show more");
	const ITEMS_BY_DEFAULT = 3;

	const expandClicked = () => {
		setExpand(v => !v);
		message === "Show more" ? setMessage("Show less") : setMessage("Show more");
	};

	const renderTableRow = (fields, row) => {
		return fields.map((field, fieldIndex) => {
			// if "iterableProperty" is set on the field, render nested table rows in the cell
			if (field.iterableProperty) {
				// if expand=true render all the nested rows, otherwise just the first 3 elements
				const iterableData = expand ? row[field.iterableProperty] : row[field.iterableProperty].slice(0, ITEMS_BY_DEFAULT);

				return <td key={fieldIndex} style={field.style || {}}>
						{iterableData.map((iterable, nestedRowIndex) => (
							<div key={nestedRowIndex}>{field.formatter(iterable)}</div>))}
							{row[field.iterableProperty].length > ITEMS_BY_DEFAULT && (
								<a className="row-expand-button" onClick={expandClicked}>{message}</a>
						)}
					</td>;
			}
			return <td key={fieldIndex} style={field.style || {}}>
				{field.formatter(row)}
			</td>;
		})
	}

	const selectRow = (row) => {
		let keys = selectedKeys;

		if (selectedKeys.includes(row.id)) {
			// Deselect
			keys = keys.filter((key) => key !== row.id);
			selection = selection.filter((selected) => selected.id !== row.id);
		} else {
			// Select
			keys = keys.slice();
			selection = selection.slice();

			keys.push(row.id);
			selection.push(row);
		}
		setSelectedKeys(keys)
		setSelection(selection)

		onRowSelect && onRowSelect(selection);
	}

	if (selectable) {
		const selected = selectedKeys.includes(row.id);

		return (
			<tr key={row.id} className={selected ? 'selected' : ''}>
				<td key="checkbox">
					<Checkbox onClick={() => selectRow(row)} selected={selected} />
				</td>
				{renderTableRow(fields, row)}
			</tr>
		);
	}

	return (
		<tr key={row.id}>
			{renderTableRow(fields, row)}
		</tr>
	);
}

export default class Table extends Component {
	static propTypes = {
		loading: PropTypes.bool,
		selectable: PropTypes.bool,
		fields: PropTypes.array.isRequired,
		data: PropTypes.array.isRequired,
		onRowSelect: PropTypes.func,
		perPage: PropTypes.number,
		defaultSort: PropTypes.number,
		defaultSortAsc: PropTypes.bool,
		update: PropTypes.bool,
		setUpdate: PropTypes.func
	};

	static defaultProps = {
		onRowSelect: () => {},
		perPage: 25,
		selectable: true,
		defaultSort: 0,
		defaultSortAsc: true
	};

	constructor(props) {
		super(props);

		const selection = props.selection || [];
		const selectedKeys = selection.map(this.keyExtractor);

		this.state = {
			data: [],
			filteredData: [],
			page: 0,
			search: '',
			selection,
			selectedKeys,
			sort: this.props.defaultSort,
			sortAsc: this.props.defaultSortAsc,
			update: false,
			setUpdate: this.props.setUpdate || (() => null)
		};

		selection.length && props.onRowSelect && props.onRowSelect(selection);
	}

	componentDidMount() {
		this.doUpdate(this.props);
	}

	componentWillReceiveProps(nextProps, nextContext) {
		this.doUpdate(nextProps);
	}

	toggleSort = (field) => () => {
		this.doSort(field, this.state.sort === field ? !this.state.sortAsc : true);
	};

	doUpdate(nextProps) {
		const willUpdateData = nextProps.data && nextProps.data.length !== this.state.data.length;
		if (nextProps.search !== this.state.search) {
			if (willUpdateData) {
				this.setState({ search: nextProps.search });
			} else {
				this.doSearch(nextProps.search);
			}
		}
		if (willUpdateData || nextProps.update) {
			this.doSort(this.state.sort, this.state.sortAsc, this.indexData(nextProps.data));
			this.state.setUpdate(false);
		}
	}

	indexData(data) {
		return data.map((row) => ({
			...row,
			_search: this.props.fields.map((field) => (field.sort(row).toString() || '').trim().toLowerCase()).join(',')
		}));
	}

	doSort(sort, sortAsc, data = null) {
		data = data || this.state.data;

		data = sortBy(data, this.props.fields[sort].sort);
		if (!sortAsc) {
			data = data.reverse();
		}

		return this.doSearch(this.state.search, data, sort, sortAsc);
	}

	filterData(query, data) {
		return data.filter((row) => row._search.indexOf(query) >= 0);
	}

	doSearch(search, data = null, sort = null, sortAsc = null) {
		const didSupplyData = data !== null;
		search = (search || '').trim().toLowerCase();
		data = data || this.state.data;
		sort = sort === null ? this.state.sort : sort;
		sortAsc = sortAsc === null ? this.state.sortAsc : sortAsc;

		// No search
		if (!search.length) {
			return this.setState({
				page: 0,
				search: '',
				data,
				sort,
				sortAsc,
				filteredData: data
			});
		}

		// Check for incremental search - filter the filtered data
		if (!didSupplyData && this.state.search && this.state.search.length && search.indexOf(this.state.search) === 0) {
			return this.setState({
				page: 0,
				search,
				filteredData: this.filterData(search, this.state.filteredData)
			});
		}

		return this.setState({
			page: 0,
			search,
			data,
			sort,
			sortAsc,
			filteredData: this.filterData(search, data)
		});
	}

	selectAll() {
		let selection = [];
		let selectedKeys = [];
		if (this.state.selectedKeys.length !== this.state.filteredData.length) {
			selection = this.state.filteredData;
			selectedKeys = this.state.filteredData.map((row) => this.keyExtractor(row));
		}

		this.setState({ selection, selectedKeys });
		this.props.onRowSelect && this.props.onRowSelect(selection);
	}

	keyExtractor(row) {
		return row.id;
	}

	isSelected(row) {
		return this.state.selectedKeys.includes(this.keyExtractor(row));
	}

	gotoPage(page) {
		this.setState({ page });
	}

	goPrev() {
		const { page } = this.state;

		if (page - 1 < 0) {
			return;
		}

		return this.gotoPage(page - 1);
	}

	goNext() {
		const pages = Math.ceil(this.state.filteredData.length / this.props.perPage);
		const { page } = this.state;

		if (page + 1 >= pages) {
			return;
		}

		return this.gotoPage(page + 1);
	}


	renderTableHeader() {
		const { selectable } = this.props;
		const allSelected = selectable && !!(this.state.selectedKeys.length && this.state.selectedKeys.length === this.props.data.length);

		return (
			<tr>
				{selectable ? (
					<th>
						<Checkbox onClick={() => this.selectAll()} selected={allSelected} />
					</th>
				) : null}
				{this.props.fields.map((field, index) => {
					if (!field.sortable) {
						return <th key={field.title}>{field.title}</th>;
					}

					let arrow = '';
					if (this.state.sort === index) {
						arrow = this.state.sortAsc ? '▾' : '▴';
					}

					return (
						<th key={field.title}>
							<button onClick={this.toggleSort(index)} className={arrow ? 'active' : ''}>
								{`${field.title} ${arrow}`}
							</button>
						</th>
					);
				})}
			</tr>
		);
	}

	renderPaginationItem(index, active) {
		return (
			<Button key={index} primary={active} onClick={() => this.gotoPage(index)}>
				{index + 1}
			</Button>
		);
	}

	renderMorePaginationItem(index) {
		return (
			<span className="pagination-more" key={index}>
				&hellip;
			</span>
		);
	}

	renderPagination() {
		const pages = Math.ceil(this.state.filteredData.length / this.props.perPage);

		let output = [];
		let index = 0;
		const showPages = 4;
		const { page } = this.state;
		while (index < pages) {
			if (index === page - showPages || index === page + showPages) {
				output.push(this.renderMorePaginationItem(index));
			}
			if (page === index || (index > page - showPages && index < page + showPages)) {
				output.push(this.renderPaginationItem(index, index === page));
			}
			index++;
		}

		if (output.length < 2) {
			return null;
		}

		return (
			<div className="pagination pagination-centered">
				<Button key="prev" disabled={page === 0} onClick={() => this.goPrev()}>
					Previous
				</Button>
				{output}
				<Button key="next" disabled={page === pages - 1} onClick={() => this.goNext()}>
					Next
				</Button>
			</div>
		);
	}

	renderRows() {
		const min = this.props.perPage * this.state.page;
		const max = min + this.props.perPage;
		return this.state.filteredData.map((row, index) => {
			if (index < min || index >= max) {
				return null;
			}
			return <RenderRow
				row={row}
				selectedKeys={this.state.selectedKeys}
				selection={this.state.selection}
				fields={this.props.fields}
				selectable={this.props.selectable}
				onRowSelect={this.props.onRowSelect}
				setSelectedKeys={selectedKeys => this.setState({selectedKeys})}
				setSelection={selection => this.setState({selection})}
			/>;
		});
	}

	renderPlaceholders() {
		if (this.props.loading) {
			return <Loader />;
		}

		if (!this.state.filteredData.length) {
			return <p style={{ fontSize: 15, textAlign: 'center' }}>No results found.</p>;
		}

		return null;
	}

	render() {
		return (
			<div>
				<table className="table parents-table">
					<thead>{this.renderTableHeader()}</thead>
					<tbody>{this.renderRows()}</tbody>
				</table>
				{this.renderPlaceholders()}
				{this.renderPagination()}
			</div>
		);
	}
}
